import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Datastore, toNumber } from '@freelancer/datastore';
import { Currency } from '@freelancer/datastore/currencies';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight } from '@freelancer/ui/text';
import { pattern, required } from '@freelancer/ui/validators';

@Component({
  template: `
    <fl-bit class="Header">
      <fl-heading
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H4"
        i18n="Create milestone modal header"
      >
        Create Milestone Payment
      </fl-heading>
    </fl-bit>
    <fl-bit class="MainBody">
      <fl-text
        [flMarginBottom]="Margin.SMALL"
        i18n="Create milestone modal body"
      >
        You can release this payment once you are 100% satisfied with the work
        provided.
      </fl-text>
    </fl-bit>
    <fl-grid>
      <fl-col [col]="12" [colTablet]="4" [flMarginBottom]="Margin.XSMALL">
        <fl-label
          [weight]="FontWeight.BOLD"
          i18n="Milestone amount input label"
        >
          Amount ({{ currencyDetails.code }})
        </fl-label>
        <fl-input
          [control]="formGroup.controls.amount"
          [type]="InputType.NUMBER"
          [beforeLabel]="currencyDetails.sign"
          [placeholder]="'25'"
        ></fl-input>
      </fl-col>
      <fl-col [col]="12" [colTablet]="8" [flMarginBottom]="Margin.XSMALL">
        <fl-label [weight]="FontWeight.BOLD" i18n="Description input label">
          Description
        </fl-label>
        <fl-input
          placeholder="e.g. Milestone for the design"
          i18n-placeholder="Example description for milestone description input"
          [flMarginBottom]="Margin.SMALL"
          [control]="formGroup.controls.description"
        ></fl-input>
      </fl-col>
    </fl-grid>
    <fl-banner-alert
      *ngIf="showError"
      [type]="BannerAlertType.ERROR"
      [closeable]="false"
      [flMarginBottom]="Margin.XSMALL"
      i18n="Create milestone modal error alert"
    >
      There was a problem creating the milestone. Please try again or
      <fl-link [link]="'/support/employer'">contact support</fl-link>.
    </fl-banner-alert>
    <fl-bit class="ActionContainer">
      <fl-button
        class="ActionButton"
        flTrackingLabel="milestoneModalCancelButton"
        [flMarginRight]="Margin.XXXSMALL"
        [color]="ButtonColor.DEFAULT"
        (click)="cancel()"
        i18n="Create milestone modal cancel button"
      >
        Cancel
      </fl-button>
      <fl-button
        class="ActionButton"
        flTrackingLabel="milestoneModalCreateButton"
        [busy]="isBusy"
        [color]="ButtonColor.SECONDARY"
        [disabled]="!formGroup.valid"
        (click)="create()"
        i18n="Create milestone modal create button"
      >
        Create Milestone
      </fl-button>
    </fl-bit>
  `,
  styleUrls: ['./create-milestone-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateMilestoneModalComponent implements OnInit {
  Margin = Margin;
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  InputType = InputType;

  isBusy = false;
  showError = false;
  formGroup: FormGroup;

  @Input() projectId: number;
  @Input() bidId: number;
  @Input() currencyDetails: Currency;
  @Input() bidderId: number;
  @Input() username: string;

  constructor(
    private modalRef: ModalRef<CreateMilestoneModalComponent>,
    private datastore: Datastore,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit() {
    // set up input formgroup
    this.formGroup = new FormGroup({
      amount: new FormControl('', [
        required('Amount field is required'),
        pattern(/^[1-9][0-9]*$/, 'Invalid amount specified'),
      ]),
      description: new FormControl('', [required('Description is required')]),
    });
  }

  create() {
    this.isBusy = true;
    // try to create a milestone via datastore
    this.datastore
      .createObject('milestones', {
        id: Date.now(),
        projectId: this.projectId,
        bidderId: this.bidderId,
        amount: toNumber(this.formGroup.controls.amount.value),
        description: this.formGroup.controls.description.value,
        currency: this.currencyDetails,
        timeCreated: Date.now(),
      })
      .then(res => {
        if (res.status === 'success') {
          this.modalRef.close(true);
        } else if (res.errorCode === 'PAYMENT_REQUIRED') {
          this.modalRef.close(true);
          // if it fails due to insufficient funds, redirect to deposit page
          // refAction = create_milestone handles the dataToken for us
          const queryParams = {
            amount: toNumber(this.formGroup.controls.amount.value),
            bidderId: this.bidderId,
            currency: this.currencyDetails.id,
            descr: this.formGroup.controls.description.value,
            id: this.projectId, // id is projectId, gets transformed to `pid` later
            refAction: 'create_milestone',
            username: this.username,
            checkBalance: true,
          };
          this.router.navigate(['/deposit'], { queryParams });
        } else {
          // otherwise show the generic error
          this.showError = true;
          this.isBusy = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  cancel() {
    this.modalRef.close(false);
  }
}
